import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { AlertTriangle, Download, RefreshCw, Star, Users } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Permission, useSession, useZone } from "@/components/session-provider";
import { formatDateTime } from "@/lib/time";

import { cn } from "@/lib/utils";
import {
  ANALYTICS_RANGES,
  DEFAULT_ANALYTICS_RANGE,
  fetchAnalyticsAgents,
  fetchAnalyticsBacklog,
  fetchAnalyticsBreakdown,
  fetchAnalyticsOverview,
  fetchAnalyticsQuality,
  formatCount,
  formatDuration,
  formatPercent,
  formatSample,
  formatSigned,
  pointChangeOf,
  rangePhrase,
  trendOf,
  type AnalyticsOverview,
  type AnalyticsQuality,
  type AnalyticsRangeDays,
} from "@/lib/analytics";
import { MetricSearchProvider } from "@/components/analytics/metric-search";
import { MetricCard, MetricRateTile, MetricTile } from "@/components/analytics/metric-card";
import { ScopeNotice } from "@/components/analytics/scope-notice";
import { BreakdownTable } from "@/components/analytics/breakdown-table";
import { AgentTable } from "@/components/analytics/agent-table";
import { AgentCardTabs } from "@/components/analytics/agent-insight-panel";
import { FilterBar, type FilterOption } from "@/components/analytics/filter-bar";
import { Movers } from "@/components/analytics/movers";
import { Standouts } from "@/components/analytics/standouts";
import { AnalyticsNarrativePanel } from "@/components/analytics/ai-narrative";
import { DeflectionSummaryCard } from "@/components/analytics/deflection-summary";
import { VolumeChart } from "@/components/analytics/volume-chart";
import { ArrivalHeatmap } from "@/components/reports/arrival-heatmap";
import { ReportBreakdownChart } from "@/components/reports/report-charts";
import { fetchReportSummary, type ReportSummary } from "@/lib/reports";
import {
  AgingChart,
  ComplianceChart,
  ResolutionDistributionChart,
  SlaRiskChart,
} from "@/components/analytics/quality-charts";
import {
  useAnalyticsSection,
  type SectionState,
} from "@/components/analytics/use-analytics-section";

/** A dashed GUID, which is what every id in this app's URLs is. */
const GUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

interface AnalyticsSearch {
  days?: AnalyticsRangeDays;
  site?: string;
  team?: string;
}

/**
 * ⚠️ Every value validated against a known set and dropped if it does not match — the same
 * rule as the queue's own search parameters, for the same reason. The range is a closed list
 * the SERVER also holds: D-52 refuses an unlisted range rather than clamping it, so a mangled
 * link must resolve to the default here rather than travel and be refused.
 *
 * ⚠️ Ids only, never names. A site or team name has no business in a link somebody pastes
 * into a chat window, and neither id grants anything: the filter narrows within the reader's
 * own query filter and RLS, and the options offered come from their own breakdown.
 */
function validateSearch(search: Record<string, unknown>): AnalyticsSearch {
  const result: AnalyticsSearch = {};

  const days = Number(search.days);
  if (ANALYTICS_RANGES.includes(days as AnalyticsRangeDays)) {
    result.days = days as AnalyticsRangeDays;
  }

  if (typeof search.site === "string" && GUID.test(search.site)) result.site = search.site;
  if (typeof search.team === "string" && GUID.test(search.team)) result.team = search.team;

  return result;
}

export const Route = createFileRoute("/analytics")({
  // The filters live in the URL so a narrowed page can be bookmarked, shared with a colleague
  // and reached again with the browser's own back button — none of which works when a filter
  // is component state. D-67 established this for `/reports`; this screen predates it.
  validateSearch,
  component: AnalyticsPage,
});

/**
 * Analytics.
 *
 * ⚠️ Nothing on this page is invented. Every figure is computed server-side from the same
 * ticket query the queue runs, under the same global query filter and the same row-level
 * security predicate — which is what makes an aggregate safe to show at all, and what makes
 * two administrators' totals legitimately differ. `ScopeNotice` says which one the reader is
 * looking at, above the first number.
 *
 * ⚠️ Gated on `report.read`, not `report.export`. Reading a chart takes nothing out of the
 * system; exporting takes PHI out in bulk and demands a written justification for it. Welding
 * the two together meant a team lead could not see how their own queue was performing without
 * being handed the largest disclosure the product permits.
 */
function AnalyticsPage() {
  const { can } = useSession();

  if (!can(Permission.ReportRead)) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <p className="rounded-lg border border-border bg-card px-4 py-6 text-sm text-muted-foreground">
          You do not have access to reporting. An administrator grants this in the app.
        </p>
      </div>
    );
  }

  return <AnalyticsContent />;
}

function AnalyticsContent() {
  /**
   * The question the page is answering, in the URL.
   *
   * ⚠️ `/reports` already does this — D-67 put its whole query in the address so that a
   * shared link shares the question and never the answer — and this screen, which is older,
   * was never brought along. So "the 90-day figures for RID" could not be sent to anybody,
   * could not be bookmarked, and was lost to a reload; each of those left somebody reading
   * the default range while believing they were reading the one they had chosen.
   *
   * ⚠️ Sharing the question is not sharing the answer, and that is what makes it safe. Every
   * figure is recomputed under the recipient's OWN query filter and RLS, so two
   * administrators following one link legitimately see different totals — which the page
   * already says, above the first number. A site or team id in the address grants nothing:
   * the filter can only narrow within what that reader could already reach.
   */
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const days = search.days ?? DEFAULT_ANALYTICS_RANGE;
  const siteId = search.site;
  const teamId = search.team;

  const [reloadToken, setReloadToken] = useState(0);

  /** Replaces rather than pushes: adjusting a filter is not a history trail. */
  const setFilters = (next: { days?: AnalyticsRangeDays; site?: string; team?: string }) =>
    void navigate({
      search: (current: AnalyticsSearch) => ({ ...current, ...next }),
      replace: true,
    });

  const setDays = (next: AnalyticsRangeDays) =>
    setFilters({ days: next === DEFAULT_ANALYTICS_RANGE ? undefined : next });

  const setSiteId = (next: string | undefined) => setFilters({ site: next });
  const setTeamId = (next: string | undefined) => setFilters({ team: next });

  const query = useMemo(() => ({ days, siteId, teamId }), [days, siteId, teamId]);

  // Five requests, independently. A slow compliance query must not take the SLA risk chart
  // with it, and each carries its own audit record of what was actually looked at — the
  // agent scorecard under its own entity type, because "who has been reading the staff
  // performance figures" is a question somebody will eventually ask the ledger.
  const overview = useAnalyticsSection(fetchAnalyticsOverview, query, reloadToken);
  const quality = useAnalyticsSection(fetchAnalyticsQuality, query, reloadToken);
  const backlog = useAnalyticsSection(fetchAnalyticsBacklog, query, reloadToken);
  const breakdown = useAnalyticsSection(fetchAnalyticsBreakdown, query, reloadToken);
  const agents = useAnalyticsSection(fetchAnalyticsAgents, query, reloadToken);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  // ⚠️ The filter options are captured from an UNFILTERED load and kept.
  //
  // They are built from the reader's own breakdown, so a dropdown can never offer a filter
  // that returns nothing and never becomes a way to enumerate an organisation somebody
  // cannot see into. But rebuilding them from a filtered response would collapse the list to
  // the one row that survived, leaving no way back out of the filter that was just applied.
  const [options, setOptions] = useState<{ sites: FilterOption[]; teams: FilterOption[] }>({
    sites: [],
    teams: [],
  });

  const unfiltered = !siteId && !teamId;
  const breakdownData = breakdown.data;

  useEffect(() => {
    if (!unfiltered || !breakdownData) return;

    const toOptions = (rows: { key: string | null; label: string }[]): FilterOption[] =>
      rows
        .filter((row): row is { key: string; label: string } => row.key !== null)
        .map((row) => ({ id: row.key, label: row.label }));

    setOptions({
      sites: toOptions(breakdownData.bySite.rows),
      teams: toOptions(breakdownData.byTeam.rows),
    });
  }, [unfiltered, breakdownData]);

  // What the reader has narrowed to, in words, for the banner that says what they are
  // looking at. Read off the captured options so it survives the filtered response.
  const activeFilters = useMemo(
    () =>
      [
        options.sites.find((s) => s.id === siteId)?.label,
        options.teams.find((t) => t.id === teamId)?.label,
      ].filter((label): label is string => Boolean(label)),
    [options, siteId, teamId],
  );

  const loading =
    overview.loading || quality.loading || backlog.loading || breakdown.loading || agents.loading;

  // Every response carries the scope, so the banner survives any one section failing. It is
  // the same value from all of them — they run the same filter over the same base query.
  const scope =
    overview.data?.scope ??
    quality.data?.scope ??
    backlog.data?.scope ??
    breakdown.data?.scope ??
    agents.data?.scope ??
    null;

  const computedAt = overview.data?.computedAtUtc ?? quality.data?.computedAtUtc ?? null;
  const zone = useZone();

  const allFailed =
    !overview.data && !quality.data && !backlog.data && !breakdown.data && !agents.data && !loading;

  /**
   * Downloads the figures as a CSV.
   *
   * ⚠️ The file is composed SERVER-SIDE, by the same builders the screen reads, and this only
   * navigates to it. Assembling it here from data the page already holds would have needed a
   * second implementation of CSV formula-injection escaping — in a language with no test
   * project in this repository, guarding a control the server already has tests for. A
   * security control with two implementations is one that agrees until somebody edits one.
   *
   * ⚠️ Not the bulk-export path. That one carries ticket rows and PHI and keeps its distinct
   * permission, justification, cap, watermark and high-severity audit event.
   */
  const exportCsv = useCallback(() => {
    const params = new URLSearchParams({ days: String(days) });
    if (siteId) params.set("siteId", siteId);
    if (teamId) params.set("teamId", teamId);

    // A navigation rather than a fetch, so the browser handles the download and the file
    // never has to be held in memory here. The session cookie travels with it.
    window.location.assign(`/api/analytics/export?${params}`);
  }, [days, siteId, teamId]);

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            How the service desk is performing, over requests you can read.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <FilterBar
            sites={options.sites}
            teams={options.teams}
            siteId={siteId}
            teamId={teamId}
            onSiteChange={setSiteId}
            onTeamChange={setTeamId}
            disabled={loading}
          />
          <RangePicker value={days} onChange={setDays} disabled={loading} />
          <button
            type="button"
            onClick={reload}
            disabled={loading}
            className="flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} aria-hidden />
            Refresh
          </button>

          <button
            type="button"
            onClick={exportCsv}
            disabled={loading || !scope}
            title="Download these figures as a CSV"
            className="flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" aria-hidden />
            CSV
          </button>
        </div>
      </header>

      {allFailed && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 px-5 py-4"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
          <div className="text-sm">
            <p className="font-medium">Reporting is unavailable</p>
            <p className="mt-0.5 text-muted-foreground">
              {overview.error ?? "Nothing could be loaded. Please try again."}
            </p>
          </div>
        </div>
      )}

      {scope && <ScopeNotice scope={scope} filters={activeFilters} />}
      {!scope && loading && <ScopeSkeleton />}

      <MetricSearchProvider>
        <div className="space-y-6">
          <HeadlineTiles state={overview} />

          <MetricCard
            metric={{
              id: "standouts",
              title: "What stands out",
              summary: "Observations taken straight from the figures below.",
              keywords: [
                "summary",
                "insight",
                "highlights",
                "overview",
                "attention",
                "what changed",
                "standout",
              ],
            }}
            footnote="Arithmetic, not a model. Every line is a rule you can check by hand against the figures on this page — nothing is generated and nothing is inferred. It says what changed and deliberately not what to do about it: that needs context this page does not have."
          >
            <Standouts
              overview={overview.data}
              quality={quality.data}
              backlog={backlog.data}
              breakdown={breakdown.data}
            />
          </MetricCard>

          {/* ⚠️ Its own card, deliberately outside the one above — that card's own footnote
              says "nothing is generated and nothing is inferred", which must stay true of it. */}
          <AnalyticsNarrativePanel query={query} />

          <DeflectionSummaryCard />

          <MetricCard
            metric={{
              id: "volume-over-time",
              title: "Raised vs. resolved",
              summary: `Daily request volume over the last ${rangePhrase(days)}.`,
              keywords: [
                "volume",
                "created",
                "opened",
                "closed",
                "throughput",
                "trend",
                "chart",
                "backlog growth",
                "intake",
              ],
            }}
            footnote="A day counts a request on the date it was raised, and again on the date it was resolved. A gap where raised sits above resolved is the backlog growing."
          >
            <SectionBody state={overview} height="h-72">
              {(data) => <VolumeChart points={data.volume} timeZoneId={data.range.timeZoneId} />}
            </SectionBody>
          </MetricCard>

          {/*
            ⚠️ The same component and the same server-side fold the reports surface uses, and
            deliberately NOT the same numbers. This screen reads every classification (D-65) and
            `/reports/request-activity` reads the caller's real clearance, so one person can see
            a busier grid here than there and both are right — which the footnote states, because
            an unstated difference between two charts of the same name is a bug report waiting to
            be filed against a working system.

            ⚠️ It does not drill through here and could not: D-65 warns that an endpoint under
            `/api/analytics` returning ticket rows would return rows the caller has no clearance
            for. That is the whole reason the drillable copy of this question lives on `/reports`.
          */}
          <MetricCard
            metric={{
              id: "arrival-pattern",
              title: "When requests arrive",
              summary: `Day of the week against hour of the day, over the last ${rangePhrase(days)}.`,
              keywords: [
                "when",
                "hour",
                "hourly",
                "time of day",
                "weekday",
                "weekend",
                "saturday",
                "sunday",
                "overnight",
                "peak",
                "busiest",
                "staffing",
                "roster",
                "cover",
                "shift",
                "heatmap",
                "arrival",
              ],
            }}
            footnote="Shading is requests per occurrence of that hour, not the raw count, so a period holding more Mondays than Saturdays does not read as a busier Monday. Counts requests arriving, never anybody working — there is no person in this grid. Figures here cover every classification you can report on, so they can exceed the same chart on the request activity report, which runs on your own clearance."
          >
            <SectionBody state={overview} height="h-72">
              {(data) => (
                <ArrivalHeatmap arrival={data.arrival} timeZoneId={data.range.timeZoneId} />
              )}
            </SectionBody>
          </MetricCard>

          <QualityTiles state={quality} days={days} />

          <MetricCard
            metric={{
              id: "sla-compliance",
              title: "SLA compliance over time",
              summary: "The share of each day's requests that met their target.",
              keywords: [
                "sla",
                "compliance",
                "target",
                "met",
                "breach",
                "on time",
                "first response",
                "resolution",
                "trend",
              ],
            }}
            footnote="Each point covers the requests raised that day whose outcome is known — answered or resolved, or the target has passed. Days where nothing has been determined yet are gaps, not zeroes."
          >
            <SectionBody state={quality} height="h-64">
              {(data) => (
                <ComplianceChart points={data.compliance} timeZoneId={data.range.timeZoneId} />
              )}
            </SectionBody>
          </MetricCard>

          <div className="grid gap-4 lg:grid-cols-2">
            <MetricCard
              metric={{
                id: "backlog-aging",
                title: "Backlog aging",
                summary: "How stale the open queue is right now.",
                keywords: [
                  "backlog",
                  "aging",
                  "stale",
                  "old",
                  "queue",
                  "open",
                  "past due",
                  "overdue",
                  "untouched",
                ],
              }}
              footnote="Point in time, not the selected range. Age runs from when the request was raised. Past due means its resolution target has already passed."
            >
              <SectionBody state={backlog} height="h-56">
                {(data) => <AgingChart bands={data.aging} />}
              </SectionBody>
            </MetricCard>

            <MetricCard
              metric={{
                id: "sla-risk",
                title: "SLA risk by priority",
                summary: "What needs a human now.",
                keywords: [
                  "sla",
                  "risk",
                  "priority",
                  "critical",
                  "breach",
                  "at risk",
                  "past due",
                  "paused",
                  "on track",
                  "triage",
                ],
              }}
              footnote="Point in time, not the selected range. At risk means three quarters of the way to the target — proportional, because targets here run from two hours to five business days. Paused means the desk is waiting on somebody else."
            >
              <SectionBody state={backlog} height="h-56">
                {(data) => <SlaRiskChart rows={data.slaRisk} />}
              </SectionBody>
            </MetricCard>
          </div>

          <MetricCard
            metric={{
              id: "resolution-distribution",
              title: "How long resolutions took",
              summary: "The spread of resolution times, not the average.",
              keywords: [
                "resolution time",
                "distribution",
                "histogram",
                "how long",
                "duration",
                "turnaround",
                "speed",
              ],
            }}
            footnote="A distribution rather than an average: resolution times are heavily skewed, so the mean lands in a gap where almost nothing sits and describes a request nobody had."
          >
            <SectionBody state={backlog} height="h-56">
              {(data) => <ResolutionDistributionChart bands={data.resolutionTimes} />}
            </SectionBody>
          </MetricCard>

          <MetricCard
            metric={{
              id: "breakdown-site",
              title: "By site",
              summary: "The same period, cut by where the request came from.",
              keywords: [
                "site",
                "location",
                "facility",
                "clinic",
                "building",
                "region",
                "per site",
                "breakdown",
                "compare",
              ],
            }}
            footnote="Requests with no site are their own row rather than being left out — site placement follows one directory rule with no fallback, so somebody the rule does not match is placed nowhere, and the size of that row is worth seeing. On time is the resolution target, over the requests in that row whose outcome is known."
          >
            <SectionBody state={breakdown} height="h-64">
              {(data) => (
                <BreakdownTable
                  group={data.bySite}
                  unitLabel="site"
                  onSelect={(key) => setSiteId(key ?? undefined)}
                  selectedKey={siteId}
                  queueParam="site"
                />
              )}
            </SectionBody>
          </MetricCard>

          <MetricCard
            metric={{
              id: "breakdown-category",
              title: "By category",
              summary: "What people are actually asking for.",
              keywords: [
                "category",
                "type",
                "issue",
                "topic",
                "reason",
                "classification",
                "sensitivity",
                "breakdown",
                "top issues",
                "trending",
              ],
            }}
            footnote="The classification is shown beside each row because a table listing HR Leadership next to Password Reset invites the two to be read as equivalent. It is not a disclosure — it is on every request in that category."
          >
            <SectionBody state={breakdown} height="h-64">
              {(data) => (
                <BreakdownTable
                  group={data.byCategory}
                  unitLabel="category"
                  noteLabel="Classification"
                />
              )}
            </SectionBody>
          </MetricCard>

          <MetricCard
            metric={{
              id: "category-movers",
              title: "Biggest movers",
              summary: "Categories that changed most against the period before.",
              keywords: [
                "trending",
                "movers",
                "rising",
                "falling",
                "change",
                "spike",
                "wow",
                "week over week",
                "hot",
                "growth",
              ],
            }}
            footnote="Compared against the equal-length period immediately before this one. A category needs at least five requests in one of the two periods to appear — below that the percentage says more about the arithmetic than about the desk."
          >
            <SectionBody state={breakdown} height="h-56">
              {(data) => <Movers rows={data.byCategory.rows} unitLabel="category" />}
            </SectionBody>
          </MetricCard>

          <MetricCard
            metric={{
              id: "breakdown-team",
              title: "By team",
              summary: "Which desk carried the work.",
              keywords: [
                "team",
                "queue",
                "desk",
                "group",
                "department",
                "workload",
                "breakdown",
                "compare",
              ],
            }}
            footnote="A team with nothing raised this period but requests still open is listed rather than dropped — it is often the row most worth reading."
          >
            <SectionBody state={breakdown} height="h-64">
              {(data) => (
                <BreakdownTable
                  group={data.byTeam}
                  unitLabel="team"
                  onSelect={(key) => setTeamId(key ?? undefined)}
                  selectedKey={teamId}
                  queueParam="team"
                />
              )}
            </SectionBody>
          </MetricCard>

          <TopRequestersCard days={days} siteId={siteId} teamId={teamId} />

          <MetricCard
            metric={{
              id: "agent-performance",
              title: "By agent",
              summary: "What each person resolved and what they are carrying now.",
              keywords: [
                "agent",
                "technician",
                "person",
                "staff",
                "workload",
                "performance",
                "leaderboard",
                "top",
                "who",
                "assignee",
                "throughput",
              ],
            }}
            footnote="Ordered by one named measure at a time, chosen above — there is no composite score. A weighted formula nobody chose, presented as a rank with a medal beside it, is a verdict about a colleague dressed as a measurement."
          >
            <SectionBody state={agents} height="h-64">
              {(data) => <AgentCardTabs table={<AgentTable data={data} />} query={query} />}
            </SectionBody>
          </MetricCard>

          <div className="grid gap-4 lg:grid-cols-2">
            <ReputationCard
              metric={{
                id: "reputation-agents",
                title: "Agent reputation",
                summary: "Average stars from requesters, per agent.",
                keywords: ["csat", "rating", "stars", "satisfaction", "feedback", "agent"],
              }}
              endpoint="/api/reputation/agents"
              siteId={siteId}
              teamId={teamId}
              icon={<Star className="h-4 w-4 text-muted-foreground" aria-hidden />}
            />
            <ReputationCard
              metric={{
                id: "reputation-teams",
                title: "Team reputation",
                summary: "Average stars from requesters, per team.",
                keywords: ["csat", "rating", "stars", "satisfaction", "feedback", "team"],
              }}
              endpoint="/api/reputation/teams"
              siteId={siteId}
              teamId={teamId}
              icon={<Users className="h-4 w-4 text-muted-foreground" aria-hidden />}
            />
          </div>
        </div>
      </MetricSearchProvider>

      {/*
        ⚠️ Outside the search provider deliberately, so filtering to one metric cannot hide
        the sentence that says what is missing. Somebody who filters to "site" and finds
        nothing should not conclude their permissions are the reason.
      */}
      <div className="rounded-xl border border-border bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
        <p>
          <span className="font-mono text-[11px]">BL-025</span> · No compliance targets are
          configured, so no figure here is marked pass or fail — a threshold invented in the browser
          would be an opinion dressed as a measurement.{" "}
          <span className="font-mono text-[11px]">BL-026</span> · There is no utilization
          percentage, because nothing records anybody&rsquo;s contracted hours — dividing logged
          time by a full-time week would make every part-time colleague read as idle. Neither is
          hidden by your permissions; they do not exist on this page.
        </p>
        {computedAt && (
          <p className="mt-1.5">
            Computed {formatDateTime(computedAt, zone)} · not cached, so these figures are as
            current as this page load.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * A card body that knows how to be empty.
 *
 * ⚠️ It renders the error INSIDE the card rather than replacing the page, so one failing
 * section reads as one failing section. It never renders stale data — `useAnalyticsSection`
 * clears it — because a chart that is quietly thirty days out of date is worse than a chart
 * that is missing.
 */
function SectionBody<T>({
  state,
  height,
  children,
}: {
  state: SectionState<T>;
  height: string;
  children: (data: T) => ReactNode;
}) {
  if (state.loading) {
    return <div className={cn("animate-pulse rounded-lg bg-muted", height)} aria-busy="true" />;
  }

  if (state.error || !state.data) {
    return (
      <div className={cn("flex items-center justify-center rounded-lg bg-muted/40", height)}>
        <p role="alert" className="px-6 text-center text-sm text-muted-foreground">
          {state.error ?? "Nothing to show."}
        </p>
      </div>
    );
  }

  return <>{children(state.data)}</>;
}

/**
 * The headline figures.
 *
 * ⚠️ The three point-in-time tiles sit in their own row, apart from the windowed ones, and
 * each carries a "now" marker. Mixed into one grid, "23 open" beside "412 raised in the last
 * 30 days" is read as two numbers about the same period by everyone who did not build it.
 */
function HeadlineTiles({ state }: { state: SectionState<AnalyticsOverview> }) {
  if (!state.data) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={cn(
              "h-24 rounded-xl border border-border bg-muted/40",
              state.loading && "animate-pulse",
            )}
          />
        ))}
      </div>
    );
  }

  const h = state.data.headline;
  const period = `vs. previous ${rangePhrase(state.data.range.days)}`;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricTile
          metric={{
            id: "kpi-raised",
            title: "Raised",
            summary: "Requests raised in the selected period.",
            keywords: ["created", "new", "intake", "volume", "opened"],
          }}
          value={formatCount(h.created)}
          trend={trendOf(h.created, h.createdPrevious)}
          hint={period}
        />
        <MetricTile
          metric={{
            id: "kpi-resolved",
            title: "Resolved",
            summary: "Requests resolved in the selected period.",
            keywords: ["closed", "completed", "fixed", "throughput"],
          }}
          value={formatCount(h.resolved)}
          trend={trendOf(h.resolved, h.resolvedPrevious)}
          goodDirection="up"
          hint={period}
        />
        <MetricTile
          metric={{
            id: "kpi-backlog-delta",
            title: "Backlog change",
            summary: "Raised minus resolved. Positive means the queue grew.",
            keywords: ["backlog", "growth", "net", "queue", "delta"],
          }}
          value={formatSigned(h.backlogDelta)}
          trend={trendOf(h.backlogDelta, h.backlogDeltaPrevious)}
          goodDirection="down"
          hint={period}
        />
        <MetricTile
          metric={{
            id: "kpi-first-response",
            title: "First response",
            summary: "Average time to the first human reply, for requests raised in the period.",
            keywords: ["frt", "response time", "reply", "sla", "answer", "wait"],
          }}
          value={formatDuration(h.averageFirstResponseMinutes)}
          trend={trendOf(h.averageFirstResponseMinutes, h.averageFirstResponseMinutesPrevious)}
          goodDirection="down"
          hint="Average, requests that have been answered"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricTile
          metric={{
            id: "kpi-resolution-time",
            title: "Time to resolve",
            summary: "Average time from raised to resolved, for requests resolved in the period.",
            keywords: ["resolution time", "cycle time", "handle", "turnaround", "duration"],
          }}
          value={formatDuration(h.averageResolutionMinutes)}
          trend={trendOf(h.averageResolutionMinutes, h.averageResolutionMinutesPrevious)}
          goodDirection="down"
          hint="Wall clock, not business hours"
        />
        <MetricTile
          metric={{
            id: "kpi-open",
            title: "Open",
            summary: "Requests not yet resolved or closed, right now.",
            keywords: ["queue", "outstanding", "wip", "in progress", "backlog"],
          }}
          value={formatCount(h.openNow)}
          pointInTime
        />
        <MetricTile
          metric={{
            id: "kpi-unassigned",
            title: "Unassigned",
            summary: "Open requests with nobody working them, right now.",
            keywords: ["triage", "untouched", "nobody", "queue"],
          }}
          value={formatCount(h.unassignedNow)}
          pointInTime
        />
        <MetricTile
          metric={{
            id: "kpi-breaching",
            title: "Past due",
            summary: "Open requests whose resolution target has already passed, right now.",
            keywords: ["sla", "breach", "overdue", "late", "missed", "target"],
          }}
          value={formatCount(h.breachingNow)}
          pointInTime
        />
      </div>
    </div>
  );
}

/**
 * The five quality rates.
 *
 * ⚠️ Every one of these is a cohort measure over requests RAISED in the period, and every one
 * carries its own denominator on screen. They deliberately do not share a denominator — the
 * population that has a first-response target is not the population that has been finished —
 * so a row of five percentages that looked comparable would be five different questions.
 */
function QualityTiles({ state, days }: { state: SectionState<AnalyticsQuality>; days: number }) {
  if (!state.data) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={cn(
              "h-36 rounded-xl border border-border bg-muted/40",
              state.loading && "animate-pulse",
            )}
          />
        ))}
      </div>
    );
  }

  const { current, previous } = state.data;

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      <MetricRateTile
        metric={{
          id: "rate-first-response-sla",
          title: "First response on time",
          summary: "Share of requests answered within their first-response target.",
          keywords: ["sla", "first response", "frt", "answered", "on time", "target", "reply"],
        }}
        value={formatPercent(current.firstResponseSla)}
        sample={formatSample(current.firstResponseSla)}
        trend={pointChangeOf(current.firstResponseSla, previous.firstResponseSla)}
        goodDirection="up"
        definition={`Of requests raised in the last ${rangePhrase(days)} that have a target and a known outcome. A late reply counts as a miss.`}
      />

      <MetricRateTile
        metric={{
          id: "rate-resolution-sla",
          title: "Resolved on time",
          summary: "Share of requests resolved within their resolution target.",
          keywords: ["sla", "resolution", "resolved", "on time", "target", "breach", "met"],
        }}
        value={formatPercent(current.resolutionSla)}
        sample={formatSample(current.resolutionSla)}
        trend={pointChangeOf(current.resolutionSla, previous.resolutionSla)}
        goodDirection="up"
        definition={`Of requests raised in the last ${rangePhrase(days)} that have a target and a known outcome — resolved, or the target has passed.`}
      />

      <MetricRateTile
        metric={{
          id: "rate-reopened",
          title: "Reopened",
          summary: "Share of finished requests that had to be reopened.",
          keywords: ["reopen", "reopened", "rework", "came back", "quality", "failed fix"],
        }}
        value={formatPercent(current.reopened)}
        sample={formatSample(current.reopened)}
        trend={pointChangeOf(current.reopened, previous.reopened)}
        goodDirection="down"
        definition="Of requests raised in the period that have been finished at least once. Counted from when this measure was added, so it climbs toward its true value as older requests turn over."
      />

      <MetricRateTile
        metric={{
          id: "rate-first-contact",
          title: "Fixed first time",
          summary: "Finished by one person, never reassigned, never reopened.",
          keywords: [
            "fcr",
            "first contact",
            "first time",
            "one touch",
            "no handoff",
            "reassignment",
            "transfer",
          ],
        }}
        value={formatPercent(current.firstContactResolution)}
        sample={formatSample(current.firstContactResolution)}
        trend={pointChangeOf(current.firstContactResolution, previous.firstContactResolution)}
        goodDirection="up"
        definition="Of finished requests raised in the period: reached one person, was never passed on, and never came back. Named for what it measures — there is no notion of a call here, so this is not first-contact resolution in the call-centre sense."
      />

      <MetricRateTile
        metric={{
          id: "rate-escalated",
          title: "Escalated",
          summary: "Share of requests somebody asked to have looked at sooner.",
          keywords: ["escalation", "escalated", "chased", "urgent", "raised", "attention"],
        }}
        value={formatPercent(current.escalated)}
        sample={formatSample(current.escalated)}
        trend={pointChangeOf(current.escalated, previous.escalated)}
        goodDirection="down"
        definition="Of all requests raised in the period. Counts requests with at least one escalation, not escalations — two people chasing one request is one escalated request."
      />
    </div>
  );
}

function RangePicker({
  value,
  onChange,
  disabled,
}: {
  value: AnalyticsRangeDays;
  onChange: (days: AnalyticsRangeDays) => void;
  disabled?: boolean;
}) {
  return (
    <div
      role="group"
      aria-label="Reporting period"
      className="flex rounded-lg border border-border bg-card p-0.5"
    >
      {ANALYTICS_RANGES.map((option) => (
        <button
          key={option}
          type="button"
          disabled={disabled}
          aria-pressed={value === option}
          onClick={() => onChange(option)}
          className={cn(
            "rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-50",
            value === option
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          {/* A 1-day window is already local-midnight-to-now (AnalyticsWindow.For), so this
              is "Today" and not a fourth range value — the same allowlist, a clearer label. */}
          {option === 1 ? "Today" : `${option}d`}
        </button>
      ))}
    </div>
  );
}

function ScopeSkeleton() {
  return <div className="h-20 animate-pulse rounded-2xl bg-muted" aria-busy="true" />;
}

/**
 * Top requesters, drawn on this page but NOT computed from this page's data.
 *
 * ⚠️ This screen is elevated to every classification (D-65), and a requester breakdown crossed
 * with that could narrow a count to one identifiable person (D-68: "if /api/reports is ever
 * elevated, this comes out first"). So the figures come from the request activity report's own
 * summary endpoint, which runs under the reader's REAL clearance in both layers. The site and
 * team filters and the range carry across; nothing else does. Totals here can therefore be
 * lower than the rest of the page, and the footnote says so.
 *
 * `byRequester` is null for the agent tier (D-68) — the card is absent then, not empty.
 */
function TopRequestersCard({
  days,
  siteId,
  teamId,
}: {
  days: AnalyticsRangeDays;
  siteId?: string;
  teamId?: string;
}) {
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setSummary(null);
    setError(null);

    const now = new Date();
    const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

    void (async () => {
      try {
        const result = await fetchReportSummary(
          {
            fromUtc: from.toISOString(),
            toUtc: now.toISOString(),
            ...(siteId ? { siteIds: [siteId] } : {}),
            ...(teamId ? { teamIds: [teamId] } : {}),
          },
          controller.signal,
        );
        if (!controller.signal.aborted) setSummary(result);
      } catch (e) {
        if (!controller.signal.aborted) {
          setError(e instanceof ApiError ? e.message : "Could not load top requesters.");
        }
      }
    })();

    return () => controller.abort();
  }, [days, siteId, teamId]);

  if (summary && !summary.byRequester) return null;

  return (
    <MetricCard
      metric={{
        id: "top-requesters",
        title: "Top requesters",
        summary: "Who is raising the most requests.",
        keywords: ["requester", "top", "frequent", "who", "raised", "volume"],
      }}
      action={<Users className="h-4 w-4 text-muted-foreground" aria-hidden />}
      footnote="Counts only — demand, never a judgement about a colleague. Read under your own clearance rather than the every-classification view the rest of this page uses (D-65), so it can be lower than the figures around it."
    >
      {error ? (
        <p role="alert" className="text-sm text-muted-foreground">
          {error}
        </p>
      ) : summary === null ? (
        <div className="h-32 animate-pulse rounded-lg bg-muted" aria-busy="true" />
      ) : (
        <ReportBreakdownChart breakdown={summary.byRequester!} />
      )}
    </MetricCard>
  );
}

interface Reputation {
  id: string;
  name: string;
  averageStars: number;
  ratingCount: number;
}

function ReputationCard({
  metric,
  endpoint,
  siteId,
  teamId,
  icon,
}: {
  metric: Parameters<typeof MetricCard>[0]["metric"];
  endpoint: string;
  siteId?: string;
  teamId?: string;
  icon: React.ReactNode;
}) {
  const [rows, setRows] = useState<Reputation[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // ⚠️ Sent and refetched on change, like every other section on this page. This card used
    // to fetch the same fixed endpoint once and never react to the site/team filter at all —
    // switching teams narrowed the agent table above it and left this one still showing every
    // team the caller can read.
    const params = new URLSearchParams();
    if (siteId) params.set("siteId", siteId);
    if (teamId) params.set("teamId", teamId);
    const query = params.toString();

    void (async () => {
      try {
        const result = await api<Reputation[]>(query ? `${endpoint}?${query}` : endpoint);
        if (!cancelled) setRows(result);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : "Could not load reputation.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [endpoint, siteId, teamId]);

  return (
    <MetricCard
      metric={metric}
      action={icon}
      // ⚠️ Said on the page, not just in a doc. Someone reading a leaderboard will otherwise
      // assume an absent name means a bad score rather than too few ratings, and act on it.
      //
      // ⚠️ And it now says these figures are SCOPED (D-64). They used to average every rating
      // in the system while the rest of the page was RLS-scoped; the number is comparable to
      // the agent table above it now, and correspondingly is not the organisation's figure
      // unless the reader can see the whole organisation. Two administrators seeing different
      // averages is legitimate — the ScopeNotice at the top of the page says which set is
      // being read, and this line stops the card being quoted as if it were absolute.
      footnote="Anyone with fewer than five ratings is left out — one five-star rating is not a reputation. These averages cover the requests you can see, like every other figure on this page, so they are not necessarily the whole organisation's. Ratings measure how a request was handled, never the person who raised it."
    >
      {error ? (
        <p role="alert" className="text-sm text-muted-foreground">
          {error}
        </p>
      ) : rows === null ? (
        <div className="h-32 animate-pulse rounded-lg bg-muted" aria-busy="true" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No ratings yet with enough responses to report.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((row) => (
            <li key={row.id} className="flex items-center gap-3 py-2.5">
              <span className="min-w-0 flex-1 truncate text-sm">{row.name}</span>

              <span className="flex items-center gap-1" aria-hidden>
                {[1, 2, 3, 4, 5].map((value) => (
                  <Star
                    key={value}
                    className={
                      row.averageStars >= value - 0.25
                        ? "h-3.5 w-3.5 fill-warning text-warning"
                        : "h-3.5 w-3.5 text-muted-foreground/30"
                    }
                  />
                ))}
              </span>

              <span className="w-16 text-right text-sm tabular-nums">
                {row.averageStars.toFixed(2)}
              </span>

              {/* The sample size, always. An average without one invites a conclusion it
                  cannot support. */}
              <span className="w-12 text-right text-xs text-muted-foreground tabular-nums">
                {row.ratingCount}
              </span>
            </li>
          ))}
        </ul>
      )}
    </MetricCard>
  );
}
